const MAX_REPORTS = 3
const MAX_STACK_LENGTH = 2000
const SENSITIVE_QUERY_KEYS = new Set(['auth', 'token'])

let installed = false
let reportsSent = 0
const reportedErrors = new Set<string>()

function ignoredMessage(message: string) {
  return message.includes('ResizeObserver loop') || message.trim() === 'Script error.'
}

function rejectionDetails(reason: unknown) {
  if (reason instanceof Error) {
    return { message: reason.message, stack: reason.stack ?? '' }
  }

  if (typeof reason === 'string') {
    return { message: reason, stack: '' }
  }

  try {
    return { message: String(reason), stack: '' }
  } catch {
    return { message: 'Unhandled promise rejection', stack: '' }
  }
}

function sanitizedUrl(value: string) {
  try {
    const url = new URL(value)

    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) url.searchParams.delete(key)
    }

    return url.toString()
  } catch {
    return ''
  }
}

function report(message: string, stack: string, source: string) {
  if (!message || ignoredMessage(message)) return

  const key = `${message}\u0000${source}`
  if (reportedErrors.has(key) || reportsSent >= MAX_REPORTS) return

  reportedErrors.add(key)
  reportsSent += 1

  try {
    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''

    void fetch('/frontend_errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        message,
        stack: stack.slice(0, MAX_STACK_LENGTH),
        url: sanitizedUrl(window.location.href),
        source,
      }),
    }).catch(() => undefined)
  } catch {
    return
  }
}

export function installErrorReporter() {
  if (installed) return

  installed = true

  window.addEventListener('error', (event) => {
    try {
      report(
        event.message,
        event.error instanceof Error ? (event.error.stack ?? '') : '',
        event.filename || 'error',
      )
    } catch {
      return
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const { message, stack } = rejectionDetails(event.reason)
      report(message, stack, 'unhandledrejection')
    } catch {
      return
    }
  })
}
